import React, { useMemo } from 'react';
import { addDays, format, isAfter, isBefore, startOfDay } from 'date-fns';
import * as Yup from 'yup';
import { useTranslation, Trans } from 'react-i18next';

import { useClientData } from '@/providers/ClientData.provider';

import {
  Select,
  TextField,
  DatepickerSimple,
  SelectOption,
  IsTodayDate,
  Timestamp,
  TextArea,
  FieldData,
  Tooltip,
  InteracWordmark,
  capitalizeFirstLetterOfWords,
  NameOfFrequency,
} from 'vopay';

import { useI18nLabels } from '@/utils/useI18nLabels';

import { PaymentFieldDefault } from './types';

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

export const useTransactionDetailsFields = (formType: 'Pay' | 'Collect'): FieldData<PaymentFieldDefault>[] => {
  const {
    response: { clientAccountsEnabled, Country, KYCLightEnabled },
  } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const { frequencyLabelMapper } = useI18nLabels();

  const scheduleTypeOptions: SelectOption<BasicOptions>[] = [
    { label: t('scheduleTypeOptions.single'), value: 'single' },
    { label: t('scheduleTypeOptions.recurring'), value: 'recurring' },
  ];

  const endingOptions: SelectOption<BasicOptions>[] = [
    { label: t('endingOptions.endDate'), value: 'End Date' },
    { label: t('endingOptions.payments'), value: 'Payments' },
  ];

  const semiMonthlyDateOptions: SelectOption<BasicOptions>[] = [
    { label: t('semiMonthlyDateOptions.firstAndFifteenth'), value: 'SemiMonthFirstAndFifteenth' },
    { label: t('semiMonthlyDateOptions.fifteenthAndLast'), value: 'SemiMonthFifteenthAndLast' },
  ];

  const frequencyOptionValues: NameOfFrequency[] = [
    'daily',
    'weekly',
    'bi-weekly',
    'monthly',
    'semi-monthly',
    'quarterly',
    'semi-annually',
    'annually',
    'weekdays',
  ];
  const frequencyOptions: SelectOption<BasicOptions>[] = frequencyOptionValues.map((value) => ({
    label: frequencyLabelMapper(value) || capitalizeFirstLetterOfWords(value),
    value,
  }));

  function isToday(dateStr: string): boolean {
    const date = startOfDay(new Date(Number(dateStr)));
    const today = startOfDay(new Date());
    return date.getTime() === today.getTime();
  }

  return useMemo(() => {
    return [
      {
        label: () => t('Fields.scheduleType.label'),
        fieldName: 'ScheduleType',
        getInputState: (ctx) =>
          (ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
          (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD')
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.PaymentSelectionMethod === 'bank' &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')),
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.value === 'recurring' && ctx.values.StartDate && isToday(ctx.values.StartDate)) {
                const selectedDate = new Date(Number(ctx.values.StartDate));
                const newDate = addDays(selectedDate, 1).getTime();
                ctx.setFieldValue('StartDate', newDate.toString());
              }
            },
          });

          return (
            <Select
              version="2"
              placeholder={t('Fields.scheduleType.placeholder')}
              options={scheduleTypeOptions}
              {...fieldData}
            />
          );
        },
        validate: () => Yup.mixed().required(),
      },
      {
        label: (ctx) =>
          ctx.values.ProvideOrRequestPaymentInformation === 'provide'
            ? formType === 'Pay'
              ? t('Fields.takeFromAccountOn.labelPay', {
                  defaultValue: 'Payment Date',
                })
              : t('Fields.takeFromAccountOn.labelCollect', {
                  defaultValue: 'Collection Date',
                })
            : t('Fields.takeFromAccountOn.labelRequest', {
                defaultValue: 'Request Transaction Date',
              }),
        fieldName: 'TakeFromAccountOn',
        serializeField: (values) =>
          values?.ScheduleType === 'single' && values?.TakeFromAccountOn
            ? format(new Date(Number(values.TakeFromAccountOn)), 'yyyy-MM-dd')
            : '',
        getInputState: (ctx) =>
          ctx.values.ScheduleType === 'single' &&
          ((ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
            (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD'))
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.ScheduleType === 'single' &&
          ctx.PaymentSelectionMethod === 'bank' &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <DatepickerSimple
              min={new Date().getTime() as Timestamp}
              {...fieldData}
              variant="outlineShadow"
              placeholder="YYYY-MM-DD"
              iconPosition="left"
              isDisabled={fieldData.disabled}
            />
          );
        },
        validate: () =>
          Yup.string()
            .test(
              'start-date-validation',
              t('Fields.takeFromAccountOn.validation', {
                date: format(new Date(), 'yyyy-MM-dd'),
              }),
              (value: string | undefined) => {
                if (!value || value === 'undefined') return false;

                const selectedDate = startOfDay(new Date(Number(value)));
                const today = startOfDay(new Date());

                return !isBefore(selectedDate, today);
              },
            )
            .required(),
      },
      {
        label: () => t('Fields.startDate.label'),
        fieldName: 'StartDate',
        serializeField: (values) =>
          values?.ScheduleType === 'recurring' && values?.StartDate
            ? format(new Date(Number(values.StartDate)), 'yyyy-MM-dd')
            : '',
        getInputState: (ctx) =>
          ctx.values.ScheduleType === 'recurring' &&
          ((ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
            (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD'))
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.ScheduleType === 'recurring' &&
          ctx.PaymentSelectionMethod === 'bank' &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')),
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              const selectedDate = new Date(Number(e.target.value));
              const endDate = new Date(Number(ctx.values.EndDate));
              if (
                isAfter(selectedDate, endDate) ||
                format(selectedDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')
              ) {
                const newDate = addDays(selectedDate, 1).getTime();
                ctx.setFieldValue('EndDate', newDate.toString());
              }
            },
          });
          return (
            <DatepickerSimple
              {...fieldData}
              min={addDays(new Date(), 1).getTime() as Timestamp}
              variant="outlineShadow"
              placeholder="YYYY-MM-DD"
              iconPosition="left"
              isDisabled={fieldData.disabled}
            />
          );
        },
        validate: () =>
          Yup.string()
            .test(
              'start-date-validation',
              t('Fields.startDate.validation', {
                date: format(new Date(), 'yyyy-MM-dd'),
              }),
              (value: string | undefined) => {
                const now = format(new Date(), 'yyyy-MM-dd');
                const dateValue = value ? new Date(Number(value)) : undefined;
                return !dateValue || format(dateValue, 'yyyy-MM-dd') >= now;
              },
            )

            .required(),
      },
      {
        label: () => t('Fields.frequency.label'),
        fieldName: 'Frequency',
        getInputState: (ctx) =>
          ctx.values.ScheduleType === 'recurring' &&
          ((ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
            (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD'))
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.PaymentSelectionMethod === 'bank' &&
          ctx.ScheduleType === 'recurring' &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select version="2" {...fieldData} options={frequencyOptions} sort={false} />
          );
        },
        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.semiMonthlyDates.label'),
        fieldName: 'SemiMonthlyDates',
        getInputState: (ctx) =>
          ((ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
            (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD')) &&
          ctx.values.ScheduleType === 'recurring' &&
          ctx.values.Frequency === 'semi-monthly'
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.PaymentSelectionMethod === 'bank' &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')) &&
          ctx.ScheduleType === 'recurring' &&
          ctx.Frequency === 'semi-monthly',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              version="2"
              placeholder={t('Fields.semiMonthlyDates.placeholder')}
              options={semiMonthlyDateOptions}
              {...fieldData}
            />
          );
        },
        validate: () => Yup.mixed().required(),
      },
      {
        label: () => t('Fields.ending.label'),
        fieldName: 'Ending',
        getInputState: (ctx) =>
          ctx.values.ScheduleType === 'recurring' &&
          ((ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
            (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD'))
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.PaymentSelectionMethod === 'bank' &&
          ctx.ScheduleType === 'recurring' &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select version="2" placeholder={t('Fields.ending.placeholder')} options={endingOptions} {...fieldData} />
          );
        },
        validate: () => Yup.mixed().required(),
      },
      {
        label: () => t('Fields.endDate.label'),
        fieldName: 'EndDate',
        serializeField: (values) =>
          values.Ending === 'End Date' && values.ScheduleType === 'recurring' && values?.EndDate
            ? format(new Date(Number(values.EndDate)), 'yyyy-MM-dd')
            : '',
        getInputState: (ctx) =>
          ((ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
            (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD')) &&
          ctx.values.ScheduleType === 'recurring' &&
          ctx.values.Ending === 'End Date'
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.PaymentSelectionMethod === 'bank' &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')) &&
          ctx.ScheduleType === 'recurring' &&
          ctx.Ending === 'End Date',
        renderField: ({ getFormikData, ctx }) => {
          const fieldData = getFormikData<string>({});
          return (
            <DatepickerSimple
              {...fieldData}
              variant="outlineShadow"
              placeholder="YYYY-MM-DD"
              iconPosition="left"
              min={addDays(new Date(Number(ctx.values.StartDate)), 1).getTime() as Timestamp}
              isDisabled={fieldData.disabled}
            />
          );
        },
        validate: () =>
          Yup.string()
            .test(
              'end-date-validation',
              t('Fields.endDate.validation'),
              (value: string | undefined, context: Yup.TestContext) => {
                if (!value || value === 'undefined') return false;

                const startDateRaw = context?.parent?.StartDate;
                if (!startDateRaw || startDateRaw === 'undefined') return false;

                const endDate = startOfDay(new Date(Number(value)));
                const startDate = startOfDay(new Date(Number(startDateRaw)));

                return !isBefore(endDate, startDate);
              },
            )
            .required(),
      },
      {
        label: () => t('Fields.endingAfterPayments.label'),
        fieldName: 'EndingAfterPayments',
        serializeField: (values) => (values.Ending === 'Payments' ? values.EndingAfterPayments || '' : ''),
        getInputState: (ctx) =>
          ctx.values.Ending === 'Payments' &&
          ((ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
            (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD'))
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.PaymentSelectionMethod === 'bank' &&
          ctx.Ending === 'Payments' &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <TextField
              variant="outlineShadow"
              preset="numericWholePositive"
              placeholder={t('Fields.endingAfterPayments.placeholder')}
              {...fieldData}
            />
          );
        },
        validate: () =>
          Yup.number()
            .transform((value) => Number(value) || 0)
            .min(1, t('Fields.endingAfterPayments.validation'))
            .required(),
      },
      {
        label: () => t('Fields.description.label'),
        fieldName: 'Description',
        getInputState: (ctx) =>
          (ctx.values.ScheduleType === 'recurring' ||
            (ctx.values.TakeFromAccountOn !== '' &&
              !IsTodayDate(format(new Date(Number(ctx.values.TakeFromAccountOn)), 'yyyy-MM-dd')))) &&
          ((ctx.values.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.values.Currency === 'CAD') ||
            (ctx.values.BankingPaymentRail === 'ach' && Country === 'US' && ctx.values.Currency === 'USD'))
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          (ctx.ScheduleType === 'recurring' ||
            (ctx.TakeFromAccountOn !== '' &&
              !IsTodayDate(format(new Date(Number(ctx.TakeFromAccountOn)), 'yyyy-MM-dd')))) &&
          ((ctx.BankingPaymentRail === 'eft' && Country === 'CA' && ctx.Currency === 'CAD') ||
            (ctx.BankingPaymentRail === 'ach' && Country === 'US' && ctx.Currency === 'USD')),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="text" placeholder="" />;
        },
        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.message.label'),
        fieldName: 'Message',
        getInputState: () => 'optional',
        showField: (ctx) => ctx.PaymentSelectionMethod === 'email',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <TextArea
              {...fieldData}
              variant="outlineShadow"
              placeholder={t('Fields.message.placeholder', 'EnterMessage')}
            />
          );
        },
        tooltip: () => (
          <Tooltip
            information={
              <Trans
                i18nKey="UseTransactionCreationFields.Fields.message.tooltip"
                components={{
                  interac: <InteracWordmark type="eTransfer" />,
                }}
                defaultValue="The recipient will see this note in their <interac></interac> notification"
              />
            }
            position="top"
            width="410px"
          />
        ),
        validate: () => Yup.string(),
      },
      {
        label: () => t('Fields.recipientName.label'),
        fieldName: 'RecipientName',
        getInputState: (ctx) => (ctx.values.BankingPaymentRail === 'rtp' ? 'required' : 'optional'),
        showField: (ctx) => ctx.BankingPaymentRail === 'rtp',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="text" placeholder="" />;
        },
        validate: () =>
          Yup.string().when(['PaymentSelectionMethod', 'BankingPaymentRail'], {
            is: (PaymentSelectionMethod: string, BankingPaymentRail: string) =>
              PaymentSelectionMethod === 'email' || BankingPaymentRail === 'rtp',
            then: Yup.string().required(),
            otherwise: Yup.string(),
          }),
      },
    ];
  }, [formType, clientAccountsEnabled, Country, KYCLightEnabled]);
};
